<script lang="ts">
  import type { Snippet } from 'svelte'

  /**
   * A section's frame, on both layouts (Phase 11 plan §5.2, §7): a 4 px ink
   * rule, the panel bar — the WX mark, the section's name in caps, and
   * whatever square ink buttons the section offers — and a scroll area under
   * it. On a phone it fills the screen above the nav and applies the notch
   * inset, once; on the desktop (`docked`) it is the 432 px panel beside the
   * rail, on the panel shadow, and the text sits on a 24 px gutter.
   */

  interface Props {
    title: string
    docked?: boolean
    actions?: Snippet
    children: Snippet
  }

  let { title, docked = false, actions, children }: Props = $props()

  let scroller: HTMLElement | undefined = $state()

  // A new section starts at its top, not wherever the last one was scrolled.
  $effect(() => {
    void title
    scroller?.scrollTo?.({ top: 0 })
  })
</script>

<section class="panel" class:docked aria-labelledby="panel-title">
  <div class="bar">
    <span class="wx-mark" aria-hidden="true">WX</span>
    <h1 id="panel-title"><span class="visually-hidden">Fusion WX: </span>{title}</h1>
    {#if actions}{@render actions()}{/if}
  </div>
  <div class="scroll" data-section-scroll bind:this={scroller}>
    {@render children()}
  </div>
</section>

<style>
  .panel {
    /* The containing block for the tables' absolutely placed captions. */
    position: relative;
    flex: 1 1 auto;
    min-height: 0;
    display: flex;
    flex-direction: column;
    padding-top: var(--safe-top);
    background: var(--color-bg);
    border-top: 4px solid var(--color-text);
  }

  .panel.docked {
    height: 100%;
    padding-top: 0;
    box-shadow: var(--panel-shadow);
  }

  .bar {
    flex: none;
    display: flex;
    align-items: center;
    gap: 8px;
    /* The ink buttons' height, so a section with none (Settings) does not
       move its title. */
    min-height: 58px;
    padding: 10px 12px 10px 16px;
  }

  .docked .bar {
    padding: 12px 16px 10px 24px;
  }

  h1 {
    flex: 1;
    margin: 0 0 0 6px;
    font-size: 13px;
    font-weight: 800;
    letter-spacing: 0.12em;
    text-transform: uppercase;
  }

  .scroll {
    flex: 1;
    min-height: 0;
    overflow-y: auto;
    /* A column that scrolls down, never sideways; anything wide scrolls in
       its own box (the hourly strip). The e2e run asserts nothing overflows. */
    overflow-x: hidden;
    overscroll-behavior: contain;
    scrollbar-width: thin;
    padding: 0 0 24px;
    /* Every section pads itself by this, so rules run to the panel's line. */
    --gutter: 16px;
  }

  .docked .scroll {
    padding-bottom: 28px;
    --gutter: 24px;
  }
</style>
